// Form fields for the Add/Edit SOW dialog. Layout follows brief §5b:
//   • Name (full-width, required)
//   • Number + Status (two-column row) — Number uses AutoIdentifierField;
//     in create mode the field starts as a quiet pill placeholder ("auto-
//     generate") with Edit affordance; in edit mode the parent passes
//     `editable={false}` so the existing number renders read-only.
//   • Start date + End date (two-column, both required)
//   • Original value (currency input with `$` prefix, narrow width per brief)
//     — disabled in edit mode with the lock-in helper text. The "ex GST"
//     suffix is rendered next to the value on the read-only display in
//     SowInlineExpand; the form input shows the suffix as a quiet meta-line
//     so the basis is visible while typing.
//   • Description (textarea)
//   • Advanced collapsible: Evidence links + Notes
//
// The form fields are stateless — state lives in the parent (modal or inline
// expand). On every change the parent re-validates; this component renders
// errors inline.

import { useId, useState, type ChangeEvent } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import EvidenceLinksField from "../shared/EvidenceLinksField";
import AutoIdentifierField from "../shared/AutoIdentifierField";
import CurrencyInput from "../shared/CurrencyInput";
import { gstSuffix } from "../../../../utils/gstSuffix";
import type { SowStatus } from "../../../../types";
import { isAllowedSowTransition } from "../../../../types/sowTransitions";
import { pastDateWarning } from "./pastDateWarning";
import type { SowFieldErrors, SowFormState } from "./sowFormState";

const STATUSES: { value: SowStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export type SowFormMode = "create" | "edit";

export interface SowFormFieldsProps {
  value: SowFormState;
  onChange: (patch: Partial<SowFormState>) => void;
  errors?: SowFieldErrors;
  disabled?: boolean;
  idPrefix: string;
  mode: SowFormMode;
  /** GST flag from the parent contract — drives the basis suffix on the
   *  Original value input. Undefined renders the placeholder "GST status
   *  not recorded". */
  gstApplies: boolean | undefined;
  /** Async uniqueness check for manual-override numbers. Optional in edit
   *  mode where the field is read-only. */
  validateUniqueNumber?: (next: string) => Promise<string | null>;
  /** When true, suppress the lock-in helper text below the disabled
   *  Original value input — the parent surfaces it elsewhere (e.g. the
   *  SowInlineExpand banner above the form). */
  suppressOriginalValueHelper?: boolean;
  /** Source SOW status when in edit mode — drives the state-machine
   *  guard on the Status dropdown so options the backend would reject
   *  are visibly disabled. Optional: in create mode the field is free
   *  (the user can pick any starting status). */
  sourceStatus?: SowStatus;
}

const ORIGINAL_VALUE_LOCK_HELPER =
  "Once saved, the value cannot be edited directly. Raise a variation to adjust later, or use Correct value for a data-entry typo (audit-logged).";

export default function SowFormFields({
  value,
  onChange,
  errors = {},
  disabled,
  idPrefix,
  mode,
  gstApplies,
  validateUniqueNumber,
  suppressOriginalValueHelper = false,
  sourceStatus,
}: SowFormFieldsProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const labelId = useId();

  const patchString =
    <K extends keyof SowFormState>(field: K) =>
    (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) =>
      onChange({ [field]: e.target.value } as Pick<SowFormState, K>);

  const isEdit = mode === "edit";
  const basis = gstSuffix(gstApplies);

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="label">
          Name <span className="text-red-600">*</span>
        </label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          className="input"
          value={value.name}
          onChange={patchString("name")}
          disabled={disabled}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? `${idPrefix}-name-error` : undefined}
        />
        {errors.name && (
          <p
            id={`${idPrefix}-name-error`}
            role="alert"
            className="text-caption text-red-600 dark:text-red-400 mt-1"
          >
            {errors.name}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <span id={`${idPrefix}-number-label`} className="label">
            SOW number
          </span>
          <AutoIdentifierField
            value={value.number}
            label="SOW number"
            editable={!isEdit}
            onCommit={(next) =>
              onChange({
                number: next,
                numberOverrideMode: !isEdit && next.trim().length > 0,
              })
            }
            validateUnique={
              isEdit
                ? undefined
                : (next) =>
                    next.trim().length === 0 || !validateUniqueNumber
                      ? Promise.resolve(null)
                      : validateUniqueNumber(next)
            }
            helperText={
              isEdit
                ? "Numbers are immutable once an SOW is created."
                : value.numberOverrideMode
                ? "Manual override. Format: SOW-YYYY-NNN. Must be unique within this contract."
                : "Auto-generated. Click Edit to use a different number."
            }
            externalError={errors.number ?? null}
            testId={`${idPrefix}-number`}
            disabled={disabled}
          />
        </div>

        <div>
          <label htmlFor={`${idPrefix}-status`} className="label">
            Status
          </label>
          <select
            id={`${idPrefix}-status`}
            className="input"
            value={value.status}
            onChange={(e) =>
              onChange({ status: e.target.value as SowStatus })
            }
            disabled={disabled}
          >
            {STATUSES.map((s) => {
              // Edit mode: disable transitions the backend would reject.
              // Create mode: every option is free — there is no prior
              // state to constrain against.
              const blocked =
                isEdit &&
                sourceStatus !== undefined &&
                !isAllowedSowTransition(sourceStatus, s.value);
              return (
                <option key={s.value} value={s.value} disabled={blocked}>
                  {s.label}
                  {blocked ? " (unavailable from current state)" : ""}
                </option>
              );
            })}
          </select>
          {isEdit && sourceStatus && (
            <p className="text-caption text-slate-500 dark:text-slate-400 mt-1">
              Some transitions are unavailable from the current state. Use
              the audit-logged correction route for state repair.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${idPrefix}-start`} className="label">
            Start date <span className="text-red-600">*</span>
          </label>
          <input
            id={`${idPrefix}-start`}
            type="date"
            className="input"
            value={value.startDate}
            onChange={patchString("startDate")}
            disabled={disabled}
            aria-invalid={!!errors.startDate}
          />
          {errors.startDate && (
            <p
              role="alert"
              className="text-caption text-red-600 dark:text-red-400 mt-1"
            >
              {errors.startDate}
            </p>
          )}
          {!errors.startDate && pastDateWarning(value.startDate) && (
            <p
              data-testid={`${idPrefix}-start-past-warning`}
              className="text-caption text-amber-700 dark:text-amber-400 mt-1"
            >
              {pastDateWarning(value.startDate)}
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-end`} className="label">
            End date <span className="text-red-600">*</span>
          </label>
          <input
            id={`${idPrefix}-end`}
            type="date"
            className="input"
            value={value.endDate}
            onChange={patchString("endDate")}
            disabled={disabled}
            aria-invalid={!!errors.endDate}
          />
          {errors.endDate && (
            <p
              role="alert"
              className="text-caption text-red-600 dark:text-red-400 mt-1"
            >
              {errors.endDate}
            </p>
          )}
          {!errors.endDate && pastDateWarning(value.endDate) && (
            <p
              data-testid={`${idPrefix}-end-past-warning`}
              className="text-caption text-amber-700 dark:text-amber-400 mt-1"
            >
              {pastDateWarning(value.endDate)}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-original-value`} className="label">
          Value <span className="text-red-600">*</span>
        </label>
        <div className="relative max-w-[240px]">
          <span
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
          >
            $
          </span>
          <CurrencyInput
            id={`${idPrefix}-original-value`}
            value={value.originalValue}
            onChange={(raw) => onChange({ originalValue: raw })}
            ariaLabel="Value"
            ariaInvalid={!!errors.originalValue}
            ariaDescribedBy={`${labelId}-helper ${labelId}-basis`}
            allowDecimal
            disabled={disabled || isEdit}
            testId={`${idPrefix}-original-value`}
          />
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span
            id={`${labelId}-basis`}
            data-testid={`${idPrefix}-original-value-basis`}
            className="text-caption text-slate-500 dark:text-slate-400"
          >
            {basis}
          </span>
        </div>
        {!suppressOriginalValueHelper && (
          <p
            id={`${labelId}-helper`}
            className="text-caption text-slate-500 dark:text-slate-400 mt-1"
          >
            {ORIGINAL_VALUE_LOCK_HELPER}
          </p>
        )}
        {errors.originalValue && (
          <p
            role="alert"
            className="text-caption text-red-600 dark:text-red-400 mt-1"
          >
            {errors.originalValue}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-description`} className="label">
          Description
        </label>
        <textarea
          id={`${idPrefix}-description`}
          className="input min-h-[80px]"
          value={value.description}
          onChange={patchString("description")}
          disabled={disabled}
        />
      </div>

      <div>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-body-sm text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-slate-50 focus:outline-hidden focus:ring-2 focus:ring-brand-400 rounded-sm"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          )}
          Advanced
        </button>
        {advancedOpen && (
          <div className="mt-3 space-y-4">
            <EvidenceLinksField
              value={value.evidenceLinks}
              onChange={(links) => onChange({ evidenceLinks: links })}
              disabled={disabled}
            />
            <div>
              <label htmlFor={`${idPrefix}-notes`} className="label">
                Notes
              </label>
              <textarea
                id={`${idPrefix}-notes`}
                className="input min-h-[80px]"
                value={value.notes}
                onChange={patchString("notes")}
                disabled={disabled}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
